import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { normalizePhone } from "@/lib/phone";
import { PRIVACY_POLICY_VERSION } from "@/lib/consent";
import {
  startLeadServerSchema,
  startLeadSourceLabel,
  generatePromoCode,
} from "@/lib/start-lead.schema";
import {
  DISPOSABLE_DOMAINS,
  MIN_FILL_MS,
  RATE_LIMIT_MAX,
  RATE_LIMIT_WINDOW_MS,
} from "@/lib/waitlist.constants";

export { startLeadServerSchema, startLeadSourceLabel, generatePromoCode };

export interface ClaimFreePassResult {
  ok: boolean;
  promoCode: string | null;
  alreadyClaimed: boolean;
}

/**
 * /start (flyer QR / NFC) signup.
 *
 * Creates a LEAD — deliberately no pilot number; that is issued in person at
 * their first session. Issues a single-use free-pass code and emails it.
 */
export const claimFreePass = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => {
    const parsed = startLeadServerSchema.safeParse(data);
    if (!parsed.success) {
      throw new Error("Please check your details and try again.");
    }
    return parsed.data;
  })
  .handler(async ({ data }): Promise<ClaimFreePassResult> => {
    const { recordAttempt } = await import("@/lib/attempts.server");
    const headers = getRequest().headers;
    const ip =
      headers.get("cf-connecting-ip") ??
      headers.get("x-forwarded-for")?.split(",")[0]?.trim() ??
      "unknown";
    const userAgent = headers.get("user-agent");
    const email = data.email.toLowerCase();

    // Every submission leaves a trace, whatever happens to it next.
    const log = (
      step: string,
      outcome: "started" | "ok" | "rejected" | "error",
      reason?: string | null,
      detail?: Record<string, unknown>,
    ) =>
      recordAttempt({
        kind: "start_lead",
        step,
        outcome,
        reason: reason ?? null,
        email,
        ip: ip === "unknown" ? null : ip,
        userAgent,
        detail: { leadSourceRaw: data.leadSourceRaw ?? null, ...(detail ?? {}) },
      });

    await log("submit", "started");

    // 1. Honeypot — pretend success so bots learn nothing.
    if (data.company && data.company.trim().length > 0) {
      await log("submit", "rejected", "honeypot");
      return { ok: true, promoCode: null, alreadyClaimed: false };
    }

    // 2. Too fast to be a human.
    if (data.elapsedMs < MIN_FILL_MS) {
      await log("submit", "rejected", "too_fast", { elapsedMs: data.elapsedMs });
      throw new Error("That submission looked automated. Please try again.");
    }

    const domain = email.split("@")[1] ?? "";
    if (DISPOSABLE_DOMAINS.has(domain)) {
      await log("submit", "rejected", "disposable_domain", { domain });
      throw new Error("Please use a permanent email address.");
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { defaultTenantId } = await import("@/lib/tenants.functions");
    const tenantId = await defaultTenantId(supabaseAdmin);

    // 3. Per-IP rate limit, shared with the waitlist form.
    if (ip !== "unknown") {
      const since = new Date(Date.now() - RATE_LIMIT_WINDOW_MS).toISOString();
      const { count } = await supabaseAdmin
        .from("signup_attempts")
        .select("id", { count: "exact", head: true })
        .eq("ip", ip)
        .gte("created_at", since);

      if ((count ?? 0) >= RATE_LIMIT_MAX) {
        await log("submit", "rejected", "rate_limited", { recentAttempts: count ?? 0 });
        throw new Error("Too many signups from this connection. Try again later.");
      }
      await supabaseAdmin.from("signup_attempts").insert({ ip, tenant_id: tenantId } as never);
    }

    const leadSourceRaw = data.leadSourceRaw?.trim() || null;
    const leadSource = startLeadSourceLabel(leadSourceRaw);
    const phone = data.phone ? normalizePhone(data.phone) : "";
    const phoneDigits = (data.phone ?? "").replace(/\D/g, "");
    const phoneKey =
      phoneDigits.length === 11 && phoneDigits.startsWith("1") ? phoneDigits.slice(1) : phoneDigits;

    // 4. One human, one record: recognise them by email, then by phone.
    let pilotId: string | null = null;
    let unsubscribeToken: string | null = null;

    const { data: byEmail } = await supabaseAdmin
      .from("pilots")
      .select("id, unsubscribe_token")
      .eq("email_normalized", email)
      .eq("tenant_id", tenantId)
      .is("archived_at", null)
      .maybeSingle();

    if (byEmail) {
      pilotId = (byEmail as { id: string }).id;
      unsubscribeToken = (byEmail as { unsubscribe_token: string | null }).unsubscribe_token;
    } else if (phoneKey.length >= 10) {
      const { data: byPhone } = await supabaseAdmin
        .from("pilots")
        .select("id, unsubscribe_token")
        .eq("phone_normalized", phoneKey)
        .eq("tenant_id", tenantId)
        .is("archived_at", null)
        .maybeSingle();
      if (byPhone) {
        pilotId = (byPhone as { id: string }).id;
        unsubscribeToken = (byPhone as { unsubscribe_token: string | null }).unsubscribe_token;
      }
    }

    let firstName = data.firstName;

    if (!pilotId) {
      const { data: inserted, error } = await supabaseAdmin
        .from("pilots")
        .insert({
          first_name: data.firstName,
          last_name: data.lastName,
          email,
          phone: phone || null,
          lead_source: leadSource,
          lead_source_raw: leadSourceRaw,
          lifecycle_stage: "lead",
          consent_policy_version: PRIVACY_POLICY_VERSION,
          consented_at: new Date().toISOString(),
        } as never)
        .select("id, first_name, unsubscribe_token")
        .single();

      if (error || !inserted) {
        console.error("start lead insert failed", error);
        await log("lead_insert", "error", error?.message ?? "insert_failed");
        throw new Error("Could not claim your pass right now. Please try again.");
      }
      pilotId = (inserted as { id: string }).id;
      unsubscribeToken = (inserted as { unsubscribe_token: string | null }).unsubscribe_token;
    } else {
      const { data: existing } = await supabaseAdmin
        .from("pilots")
        .select("first_name")
        .eq("id", pilotId)
        .maybeSingle();
      firstName = (existing as { first_name?: string } | null)?.first_name || data.firstName;
    }

    // 4b. A pilot's share link: link the two people for life, once.
    const refCode = data.refCode?.trim() || null;
    if (refCode) {
      try {
        const referrals = await import("@/lib/referrals.server");
        const inviter = await referrals.resolveReferralCode(supabaseAdmin, refCode);
        if (inviter && inviter.pilotId !== pilotId) {
          await referrals.linkReferral(supabaseAdmin, {
            inviterPilotId: inviter.pilotId,
            inviteePilotId: pilotId,
            tenantId,
            source: "share_link",
          });
        }
      } catch (e) {
        console.error("start lead referral link failed", e);
      }
    }

    // 5. Attribution: every /start submit is a touch.

    try {
      const { recordTouch, findCampaignId } = await import("@/lib/touches.server");
      const utmCampaign = new URLSearchParams(leadSourceRaw ?? "").get("utm_campaign");
      const campaignId = utmCampaign
        ? await findCampaignId(supabaseAdmin, tenantId, {
            trackingKey: utmCampaign,
            name: utmCampaign,
          })
        : null;

      await recordTouch(supabaseAdmin, {
        pilotId,
        campaignId,
        touchType: "form_submit",
        note: `Free pass claimed (${leadSource})`,
      });

      // Credit the campaign that actually brought them in — only if nothing
      // has claimed them yet, so first touch stays first touch.
      if (campaignId) {
        await supabaseAdmin
          .from("pilots")
          .update({ first_touch_campaign_id: campaignId } as never)
          .eq("id", pilotId)
          .is("first_touch_campaign_id", null);
      }
    } catch (e) {
      console.error("start lead touch failed", e);
    }

    // 6. Their own single-use code — reuse an unredeemed one instead of stacking.
    const { data: activeCode } = await supabaseAdmin
      .from("lead_promo_codes")
      .select("code, expires_at, redeemed_at")
      .eq("pilot_id", pilotId)
      .eq("kind", "proving_ground_intro")
      .is("redeemed_at", null)
      .maybeSingle();

    let promoCode = (activeCode as { code?: string } | null)?.code ?? null;
    let expiresAt = (activeCode as { expires_at?: string } | null)?.expires_at ?? null;
    const alreadyClaimed = Boolean(promoCode);

    if (!promoCode) {
      for (let attempt = 0; attempt < 5 && !promoCode; attempt++) {
        const candidate = generatePromoCode();
        const { data: row, error } = await supabaseAdmin
          .from("lead_promo_codes")
          .insert({
            tenant_id: tenantId,
            pilot_id: pilotId,
            code: candidate,
            kind: "proving_ground_intro",
          } as never)
          .select("code, expires_at")
          .single();
        if (!error && row) {
          promoCode = (row as { code: string }).code;
          expiresAt = (row as { expires_at: string }).expires_at;
        } else if (error && error.code !== "23505") {
          console.error("promo code insert failed", error);
          break;
        }
      }
    }

    // 7. Deliver the pass, pointing at the next intro session when one exists.
    if (promoCode) {
      const { data: nextSession } = await supabaseAdmin
        .from("events")
        .select("title, slug")
        .eq("tenant_id", tenantId)
        .eq("is_intro_session", true)
        .eq("status", "planned")
        .gte("starts_at", new Date().toISOString())
        .order("starts_at", { ascending: true })
        .limit(1)
        .maybeSingle();

      const { sendStartWelcomeEmail } = await import("@/lib/start-lead-email.server");
      await sendStartWelcomeEmail({
        id: pilotId,
        firstName,
        email,
        promoCode,
        expiresAt,
        nextSessionTitle: (nextSession as { title?: string } | null)?.title ?? null,
        nextSessionSlug: (nextSession as { slug?: string } | null)?.slug ?? null,
        unsubscribeToken,
      });
      await log("submit", "ok", null, { pilotId, promoCode, alreadyClaimed });
    } else {
      await log("promo_code", "error", "no_code_issued", { pilotId });
    }

    return { ok: true, promoCode, alreadyClaimed };
  });

/** Public read: upcoming Proving Ground Intro and Level 0 sessions for the permanent /start page. */
export const listIntroSessions = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("events")
    .select("id, title, slug, starts_at, ends_at, location, capacity")
    .eq("event_type", "proving_ground")
    .eq("status", "planned")
    .eq("visibility", "public")
    .gte("starts_at", new Date().toISOString())
    .order("starts_at", { ascending: true })
    .limit(12);

  if (error) {
    console.error("intro session read failed", error);
    return { sessions: [] as IntroSession[] };
  }
  return { sessions: (data ?? []) as unknown as IntroSession[] };
});

export interface IntroSession {
  id: string;
  title: string;
  slug: string;
  starts_at: string;
  ends_at: string | null;
  location: string | null;
  capacity: number;
}

/** Crew/admin: the free-pass codes issued to one person. */
export const listPilotPromoCodes = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { pilotId: string }) => data)
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await context.supabase
      .from("lead_promo_codes")
      .select("id, code, kind, issued_at, expires_at, redeemed_at")
      .eq("pilot_id", data.pilotId)
      .order("issued_at", { ascending: false });

    if (error) {
      console.error("promo code read failed", error);
      return { codes: [] as PromoCodeRow[] };
    }
    return { codes: (rows ?? []) as unknown as PromoCodeRow[] };
  });

/** Crew/admin: mark a free pass as used. A used code can't be reused. */
export const redeemPromoCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("lead_promo_codes")
      .update({ redeemed_at: new Date().toISOString(), redeemed_by: context.userId } as never)
      .eq("id", data.id)
      .is("redeemed_at", null)
      .select("id")
      .maybeSingle();

    if (error) {
      console.error("promo code redeem failed", error);
      throw new Error("Could not mark that pass as used.");
    }
    if (!row) throw new Error("That pass has already been used.");
    return { ok: true };
  });

export interface PromoCodeRow {
  id: string;
  code: string;
  kind: string;
  issued_at: string;
  expires_at: string;
  redeemed_at: string | null;
}
